import { useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import { FormLayout, Input, Label, Larger } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useToasts from '@/hooks/useToasts';
import { api } from '@/utils/api';
import { getNetworkLabel } from '@/utils/dq';
import { EditorialFacebookPostNetwork } from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';

const Dialog = styled(StyledDialog)`
  width: 464px;
  text-align: left;
`;

const Heading = styled(Text)`
  margin: 0 0 16px;
  text-align: center;
`;

const Footer = styled.div`
  padding: 24px 32px 32px;
  display: flex;
  box-shadow: 0px -1px 8px 0px #2222220f;
  gap: 24px;
  margin: 56px -24px -40px;
`;

const FooterButton = styled(Button)`
  flex: 1;
`;

const NetworkInput = styled(Input)`
  text-transform: capitalize;
`;

export type FormDataT = {
  start_time: string | null;
};

interface Props {
  onClose: VoidFunction;
  network: EditorialFacebookPostNetwork;
  channelId: number;
  frequency: number;
}

const EnablingModal: FC<Props> = ({
  onClose,
  network,
  channelId,
  frequency,
}) => {
  const alerts = useAlerts();
  const toasts = useToasts();
  const queryClient = useQueryClient();

  const {
    control,
    formState: { isSubmitting },
    setError,
    handleSubmit,
  } = useForm<FormDataT>({
    defaultValues: {
      start_time: null,
    },
  });

  const onSubmit = handleSubmit(async ({ start_time }) => {
    try {
      const { error } = await api.post<object, { error?: string }>(
        `/v1/editorial/queue/status`,
        {
          page_id: channelId,
          enabled: true,
          start_time,
          network,
        },
      );

      if (error) {
        return alerts.error('Failed to enabled the queue', error);
      }

      await queryClient.invalidateQueries([
        'editorial_distribution_queue_channels',
      ]);

      onClose();

      toasts.create('success', 'Queue started');
    } catch (err) {
      handleFormErrors(err, setError, alerts);
    }
  });

  const networkLabel = getNetworkLabel(network);

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Heading $type="heading2">Frequency</Heading>
        <FormLayout>
          <FormField<FormDataT>
            type="datepicker"
            datepickerType="datetime"
            control={control}
            name="start_time"
            label="Start Date & Time"
            placeholder="Select"
          />
          <Larger>
            <Label>End Date & Time</Label>
            <Input disabled defaultValue="--:--" />
          </Larger>
          <Larger>
            <Label>Network</Label>
            <NetworkInput disabled value={networkLabel} />
          </Larger>
          <Larger>
            <Label>Frequency</Label>
            <Input disabled defaultValue={`${frequency} min`} />
          </Larger>
        </FormLayout>
        <CloseButton onClick={onClose}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
        <Footer>
          <FooterButton
            type="button"
            color="primary"
            disabled={isSubmitting}
            loading={isSubmitting}
            onClick={onSubmit}
          >
            Start
          </FooterButton>
          <FooterButton type="button" color="secondary" onClick={onClose}>
            Cancel
          </FooterButton>
        </Footer>
      </Dialog>
    </Modal>
  );
};

export default EnablingModal;
